import React, { useState } from 'react';
import { Search, Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { Tag } from '@/types';

interface SearchableFilterSectionProps {
  title: string;
  tags: Tag[];
  selectedTagIds: Set<string>;
  onToggle: (tagId: string) => void;
  maxVisible?: number;
  showVideoIcon?: (tag: Tag) => boolean;
}

const SearchableFilterSection: React.FC<SearchableFilterSectionProps> = ({
  title,
  tags,
  selectedTagIds,
  onToggle,
  maxVisible = 6,
  showVideoIcon
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTags = searchQuery 
    ? tags.filter(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : tags;
  
  // Tags to show by default (first maxVisible)
  const visibleTags = tags.slice(0, maxVisible);
  
  // Selected tags that aren't in the visible list
  const selectedNotVisible = tags.filter(
    t => selectedTagIds.has(t.id) && !visibleTags.some(v => v.id === t.id)
  );

  const remainingCount = tags.length - maxVisible;

  if (tags.length === 0) {
    return (
      <Collapsible defaultOpen className="border-b border-border pb-4">
        <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:text-primary transition-colors">
          <span className="font-semibold text-sm">{title}</span>
          <ChevronDown className="h-4 w-4 transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <p className="text-xs text-muted-foreground italic">No filters available yet</p>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <Collapsible defaultOpen className="border-b border-border pb-4">
      <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:text-primary transition-colors">
        <span className="font-semibold text-sm">{title}</span>
        <ChevronDown className="h-4 w-4 transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2 space-y-3">
        {/* Search Button - only show if more than maxVisible tags */}
        {tags.length > maxVisible && (
          <Popover open={isSearchOpen} onOpenChange={setIsSearchOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                className="w-full justify-start text-muted-foreground font-normal h-8"
              >
                <Search className="mr-2 h-3.5 w-3.5" />
                Search {tags.length} options...
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="start">
              <Command>
                <CommandInput 
                  placeholder={`Search ${title.toLowerCase()}...`}
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
                <CommandList>
                  <CommandEmpty>No options found.</CommandEmpty>
                  <CommandGroup>
                    {filteredTags.slice(0, 20).map(tag => (
                      <CommandItem
                        key={tag.id}
                        value={tag.name}
                        onSelect={() => {
                          onToggle(tag.id);
                          setSearchQuery('');
                        }}
                        className="flex items-center gap-2"
                      >
                        <div className={cn(
                          "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                          selectedTagIds.has(tag.id) 
                            ? "bg-primary text-primary-foreground" 
                            : "opacity-50"
                        )}>
                          {selectedTagIds.has(tag.id) && <Check className="h-3 w-3" />}
                        </div>
                        <span className="flex-1 truncate">{tag.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}

        {/* Selected tags not in visible list */}
        {selectedNotVisible.length > 0 && (
          <div className="space-y-1.5">
            <span className="text-xs text-muted-foreground">Selected</span>
            {selectedNotVisible.map(tag => (
              <div key={tag.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`tag-selected-${tag.id}`}
                  checked={true}
                  onCheckedChange={() => onToggle(tag.id)}
                />
                <label
                  htmlFor={`tag-selected-${tag.id}`}
                  className="text-sm cursor-pointer flex-1"
                >
                  {tag.name}
                </label>
              </div>
            ))}
          </div>
        )}

        {/* Visible tags (first maxVisible) */}
        <div className="space-y-1.5">
          {visibleTags.map(tag => (
            <div key={tag.id} className="flex items-center space-x-2">
              <Checkbox
                id={`tag-${tag.id}`}
                checked={selectedTagIds.has(tag.id)}
                onCheckedChange={() => onToggle(tag.id)}
              />
              <label
                htmlFor={`tag-${tag.id}`}
                className="text-sm cursor-pointer flex-1 flex items-center gap-2"
              >
                {tag.name}
                {showVideoIcon?.(tag) && (
                  <span className="text-amber-500">🎥</span>
                )}
              </label>
            </div>
          ))}
        </div>

        {/* Show count of additional options */}
        {remainingCount > 0 && (
          <p className="text-xs text-muted-foreground pt-1">
            + {remainingCount} more
          </p>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default SearchableFilterSection;
